import { Injectable, OnDestroy, Inject } from '@angular/core';
import { wamp_last_error_code, wamp_init, wamp_connect, wamp_isConnected, wamp_disconnect, wamp_dispose, wamp_isInit, wamp_call } from './wamp_client';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { LibConfigService, LibConfig } from './vaelsys-webrtc-lib.module';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

export type ConnectionState =
| 'new'                   // Se acaba de crear la llamada
| 'connecting'            // Se está conectando al servidor de websockets
| 'connected'             // Se ha conectado, se va a iniciar la llamada
| 'streamconnecting'      // Se está iniciando la conexión webrtc
| 'streamconnected'       // Connexión webrtc establecida
| 'streaming'             // Reproduciendo vídeo
| 'streamdisconnected'    // Se ha desconectado la conexión webrtc
| 'disconnected'          // Se ha desconectado del servidor de websockets
| 'closed'                // La conexión webrtc se ha cerrado
| 'failed';               // Ha habido un error en alguno de los pasos

export type CallConnectionState =
| 'streamconnecting'      // Se está iniciando la conexión webrtc
| 'streamconnected'       // Connexión webrtc establecida
| 'streaming'             // Reproduciendo vídeo
| 'streamdisconnected'    // Se ha desconectado la conexión webrtc
| 'closed'                // La conexión webrtc se ha cerrado
| 'failed';               // Ha habido un error en alguno de los pasos

export interface CallError {
  errorCode?: string,
  errorString?: string
};

export interface VaelsysWebrtcComponent {
  cleanup() : Promise<boolean>;
}

interface CacheEntry {
  date: Date,
  entry: any
}

@Injectable({
  providedIn: 'root'
})
export class VaelsysWebrtcLibService implements OnDestroy{
  connectionState : BehaviorSubject<ConnectionState>;

  // Private props
  private readonly _infoExpirationTime : number = 30*1000;
  private _lastError: CallError | undefined;
  private _user: string | undefined;
  private _cache : Map<string, CacheEntry>;
  private _components : VaelsysWebrtcComponent[] = [];

  constructor(@Inject(LibConfigService) private config: LibConfig, private http: HttpClient) {
    this.connectionState = new BehaviorSubject<ConnectionState>('new');
    this._cache = new Map<string, CacheEntry>();
  }

  ngOnInit() {}

  ngOnDestroy() {
    this.dispose();
  }

  async init(username: string) : Promise<boolean> {
    let ret : boolean = false;

    // Call vSBN to fetch the user
    // Call the vSBN rest API to fetch the device information
    let res: any;
    this._setState('new');
    try {
      res = await firstValueFrom(this.http.get<any>(this.config.apiUrl+'/login', {
        params: {
          'user': username
        }
      }));

      this._lastError = undefined;
    } catch (e) {
      console.error(e);
      if (e instanceof HttpErrorResponse) {
        this._setLastError(e.statusText, e.error);
      } else {
        this._setLastError('Unknown', (e as Error).message);
      }
      
      this._setState('failed');
      this._setState('new');
      return ret;
    }

    const vscToken = res.token;

    // Connect to wamp
    this._setState('connecting');

    if (wamp_isInit()) {
      // Otherwise, the VSC token won't be refreshed
      console.warn('Disposing wamp before initing it again');
      await wamp_dispose();
    }

    let ok = await wamp_init(this.config.wsUrl, this.config.wsRealm, username, vscToken, {
      maxRetries: 10,
      reconnectInterval: 500,
      onClose: () => {
        // We are disconnected from the wamp router, so we need to change the state accordingly
        // this._lastError = {
        //   errorCode: 'Wamp_Disconnected',
        //   errorString: 'Can\'t connect to websocket server'
        // };
        // this._setState('failed');
        this._setState('disconnected');
        console.warn('WebSocket disconnected');
      },
      tickUserOnHeartbeat: true
    });

    if (ok) {
      ok = await wamp_connect();
    } else {
      console.error('Error initializing wamp');
    }

    if (!ok) {
      console.error('Error connecting ' + wamp_last_error_code());
      
      this._setLastError('Wamp_'+wamp_last_error_code(), 'Failed to connect to the Websocket Server');
      this._setState('failed');
      this._setState('new');
      return false;
    } else {
      this._lastError = undefined;
    }
    
    this._user = username;
    this._checkConnectedState();
    
    ret = true;
    return ret;
  }

  async dispose() : Promise<boolean> {
    let ret : boolean = false;
    
    this._lastError = undefined;
    this._user = undefined;
    this._cache.clear();

    // Cleanup every call
    const calls = [...this._components];
    for (const c of calls) {
      await c.cleanup();
    }

    if (wamp_isConnected()) {
      try {
        await wamp_call('logout');
      } catch(error) {
        console.error('Failed to logout', error);
      }

      await wamp_disconnect();
    }

    if (wamp_isInit()) {
      await wamp_dispose();
    }

    ret = true;
    return ret;
  }

  isInit() : boolean {
    return wamp_isInit();
  }

  isConnected() : boolean {
    return this.isInit() && wamp_isConnected();
  }

  async assertIsConnected() : Promise<boolean> {
    let ret = false;

    if (this.isInit()) {
      if (wamp_isConnected()) {
        ret = true;
      } else {
        console.warn('Not connected to wamp. Trying to connect');
        this._setState('connecting');
        ret = await wamp_connect();

        if (!ret) {
          console.error('Error connecting ' + wamp_last_error_code());
          this._setLastError('Wamp_'+wamp_last_error_code(), 'Failed to connect to the Websocket Server');
          this._setState('failed');
        }
      }
    } else {
      console.error('Service not init');
    }

    this._checkConnectedState();

    return ret;
  }

  lastError() : CallError | undefined {
    return this._lastError;
  }

  attachComponent(component: VaelsysWebrtcComponent) {
    this._components.push(component);
  }

  detachComponent(component: VaelsysWebrtcComponent) {
    this._components = this._components.filter(c => {return c !== component});
  }

  async check(deal_id: string, s_ins: number, s_cam: number, device_id: string, validation_code: string, check_online: boolean=true) : Promise<number> {
    let ret = -1;

    if (!this.isInit()) {
      this._setLastError('InvalidCall', 'Service not init');
      return ret;
    }

    if (!(await this.assertIsConnected())) {
      this._setLastError('InvalidCall', 'Service not connected');
      return ret;
    }

    if (this._cache.has(device_id)) {
      const age = Date.now() - this._cache.get(device_id)!.date.getTime();
      if (age < this._infoExpirationTime) {
        console.log('Fetching device from cache', device_id);
        return 200;
      } else {
        console.log('Device outdated, fetching again', age);
        this._cache.delete(device_id);
      }
    }

    let res : any;
    try {
      const response = await firstValueFrom(this.http.get<any>(this.config.apiUrl+'/device', {
        params: {
          'deal_id': deal_id,
          's_ins': s_ins,
          'user': this._user!,
          's_cam': s_cam,
          'device_id': device_id,
          'validation_code': validation_code,
          'init_device': check_online ? 'true' : 'false'
        },
        observe: 'response'
      }));

      res = response.body;
      ret = response.status;
      this._lastError = undefined;

    } catch (e) {
      console.error(e);
      if (e instanceof HttpErrorResponse) {
        this._lastError = {
          errorCode: e.statusText,
          errorString: e.error
        };
        ret = e.status;
      } else {
        this._setLastError('Unknown', (e as Error).message);
      }
      
      // this._setState('failed');
      return ret;
    }


    const deviceInfo = res.device;
    this._cache.set(device_id, {
      date: new Date(),
      entry: deviceInfo
    });

    return ret;
  }

  async deviceInfo(deal_id: string, s_ins: number, s_cam: number, device_id: string, validation_code: string) : Promise<any> {
    let ret;

    if (!this.isInit()) {
      this._setLastError('InvalidCall', 'Service not init');
      return ret;
    }

    // Check the device
    await this.check(deal_id, s_ins, s_cam, device_id, validation_code, false);

    // Fetch the info
    ret = this._cache.get(device_id)?.entry;

    return ret;
  }

  private _setLastError(code: string, message: string) {
    this._lastError = {
      errorCode: code,
      errorString: message
    };
  }

  setCallState(state: CallConnectionState) {
    this._setState(state);
  }

  private _setState(state: ConnectionState) {
    if (this.connectionState.value == 'failed' && state != 'new' && state != 'disconnected' && state != 'connected') {
      console.log('Failed, new state ignored: ' + state);
      return;
    } else if (this.connectionState.value == 'disconnected' && state != 'failed' && state != 'new' && state != 'connecting' && state != 'connected') {
      console.log('Disconnected client, new state ignored: ' + state);
      return;
    }

    if (this.connectionState.value != state) {
      this.connectionState.next(state);
    }

    if (state == 'failed' || state == 'closed' || state == 'streamdisconnected') {
      this._checkConnectedState();
    }
  }

  private _checkConnectedState() {
    if (wamp_isConnected()) {
      const state = this.connectionState.value;
      if (state != 'streamconnecting' && state != 'streamconnected' && state != 'streaming') {
        this._setState('connected');
      }
    } else {
      this._setState('disconnected');
    }
  }
}
